// My Profile — current admin's self-management page. Same TVP2 §9
// styling as AdminUserEdit/List: bordered Paper cards, MUI standard
// inputs (underline), Bootstrap-blue Save / grey Reset buttons.
//
// Two sections:
//   1. Profile      — editable User Name, E-mail, First/Last Name, Phone
//   2. Forgot Password — Send OTP to own email, then OTP + New + Confirm.
//      Email is locked to the current admin's address (you can't send
//      a verification code to somebody else's mailbox from this page).

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";
import { useAdminMe } from "@/hooks/useAdminMe";
import {
  CaptchaWidget,
  type CaptchaWidgetHandle,
} from "@/components/CaptchaWidget";

// Map server error codes to operator-readable copy. Falls back to the raw
// code (or generic message) when we don't have a translation, so callers
// always see SOMETHING meaningful — not a JSON blob or "[object Object]".
function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "admin_recovery_locked":
      return "Too many recovery attempts on this admin account. For security it's locked for a few hours — try again later or ask another admin to reset your password from the user list.";
    case "too_many_requests":
      return "Too many requests. Please wait a minute and try again.";
    case "no_email_on_file":
      return "Your account has no email on file, so a verification code can't be sent.";
    case "invalid_credentials":
      return "The old password you entered is incorrect.";
    case "otp_provider_unavailable":
      return "Verification email provider is temporarily unavailable. Try again shortly.";
    case "bad_request":
      return "The request is invalid. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

interface ProfileForm {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export function MyProfile() {
  const { data: me, isLoading, refetch } = useAdminMe();
  const { show, ToastEl } = useToast();

  const splitFromDisplay = (full: string | null) => {
    if (!full) return { first: "", last: "" };
    const ix = full.indexOf(" ");
    if (ix === -1) return { first: full, last: "" };
    return { first: full.slice(0, ix), last: full.slice(ix + 1).trim() };
  };

  const initialFromMe = (): ProfileForm | null => {
    const p = me?.profile;
    if (!p) return null;
    const fallback = splitFromDisplay(p.display_name ?? null);
    return {
      username: p.username ?? "",
      first_name: p.first_name ?? fallback.first,
      last_name: p.last_name ?? fallback.last,
      email: p.email ?? "",
      phone: p.phone ?? "",
    };
  };

  const [form, setForm] = useState<ProfileForm | null>(null);
  useEffect(() => {
    if (me?.profile) setForm(initialFromMe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.profile?.id]);

  const original = useMemo(initialFromMe, [me?.profile?.id]);

  const profileSave = useMutation({
    mutationFn: async () => {
      if (!form || !original || !me?.profile?.id) return;
      // username is read-only on this form (locked after creation), so we
      // intentionally don't diff it into the patch body.
      const body: Record<string, unknown> = {};
      if (form.first_name !== original.first_name)
        body.first_name = form.first_name;
      if (form.last_name !== original.last_name)
        body.last_name = form.last_name;
      if (form.email !== original.email) body.email = form.email;
      if (Object.keys(body).length === 0) {
        show("No changes to save", "info");
        return;
      }
      await apiFetch(`/v1/admin/admin-users/${me.profile.id}`, {
        method: "PATCH",
        body,
      });
    },
    onSuccess: () => {
      show("Profile updated", "success");
      refetch();
    },
    onError: (err) => show(friendlyError(err, "Update failed"), "error"),
  });

  // Forgot Password (OTP flow). Reuses the public auth endpoints since the
  // verification code is the auth factor — admin's logged-in JWT does NOT
  // grant the password rotation by itself (defence-in-depth: a stolen JWT
  // alone can't change the password without OTP from email).
  //
  // Captcha is required by /v1/auth/forgot-password and /v1/auth/reset-password
  // (Turnstile siteverify), so we render a CaptchaWidget and pass the token on
  // every call. Without this the request fails with `captcha_failed` (403).
  const [otp, setOtp] = useState({ code: "", next: "", confirm: "" });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);
  const otpAddress = me?.profile?.email ?? "";
  const otpMatch = otp.next === otp.confirm;
  const otpReady =
    otp.code.length === 6 &&
    otp.next.length >= 8 &&
    otpMatch &&
    Boolean(otpAddress) &&
    captchaToken !== null;

  const sendCode = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/auth/forgot-password`, {
        method: "POST",
        body: { identifier: otpAddress },
        captchaToken: captchaToken ?? undefined,
      }),
    onSuccess: () => {
      show(
        `Verification code sent to ${otpAddress}. Check your inbox.`,
        "success",
      );
      // Turnstile tokens are single-use — burn the current one so the
      // next mutation gets a fresh challenge.
      captchaRef.current?.reset();
      setCaptchaToken(null);
    },
    onError: (err) => {
      show(friendlyError(err, "Failed to send code"), "error");
      captchaRef.current?.reset();
      setCaptchaToken(null);
    },
  });

  const otpReset = useMutation({
    mutationFn: () =>
      apiFetch(`/v1/auth/reset-password`, {
        method: "POST",
        body: {
          identifier: otpAddress,
          otp_code: otp.code,
          new_password: otp.next,
        },
        captchaToken: captchaToken ?? undefined,
      }),
    onSuccess: () => {
      show("Password reset. You'll be signed out of other sessions.", "success");
      setOtp({ code: "", next: "", confirm: "" });
      captchaRef.current?.reset();
      setCaptchaToken(null);
    },
    onError: (err) => {
      show(friendlyError(err, "Reset failed"), "error");
      captchaRef.current?.reset();
      setCaptchaToken(null);
    },
  });

  if (isLoading || !form) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Profile card */}
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="User Name"
              helperText="Cannot be changed after creation"
              disabled
              value={form.username}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="E-mail"
              type="email"
              inputProps={{ maxLength: 254 }}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="First Name"
              inputProps={{ maxLength: 80 }}
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="Last Name"
              inputProps={{ maxLength: 80 }}
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              label="Phone"
              helperText="E.164 format e.g. +85255551234"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              disabled
            />
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              disabled={profileSave.isPending}
              sx={primaryBtn}
              onClick={() => profileSave.mutate()}
            >
              Save
            </Button>
            <Button
              variant="contained"
              sx={greyBtn}
              onClick={() => original && setForm(original)}
            >
              Reset
            </Button>
          </Stack>
        </Paper>

        {/* Forgot Password card */}
        <Paper elevation={0} sx={cardSx}>
          <Typography sx={sectionHeading}>Forgot Password</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 2,
              alignItems: "end",
            }}
          >
            <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
              <TextField
                variant="standard"
                fullWidth
                label="Send verification code to email"
                value={otpAddress}
                disabled
                helperText="Locked to your account email"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ alignSelf: "center" }}>
              <Button
                variant="contained"
                sx={primaryBtn}
                disabled={
                  !otpAddress || !captchaToken || sendCode.isPending
                }
                onClick={() => sendCode.mutate()}
              >
                Send Code
              </Button>
            </Box>
          </Box>
          {/* Turnstile widget — backend captcha is required on both
              /v1/auth/forgot-password and /v1/auth/reset-password. A single
              widget covers both calls; we reset it after each mutation so
              the next call gets a fresh token (Turnstile tokens are
              single-use server-side). */}
          <Box sx={{ mb: 2 }}>
            <CaptchaWidget
              ref={captchaRef}
              onToken={(t) => setCaptchaToken(t)}
              onError={() =>
                show(
                  "Unable to verify captcha. Please refresh and try again.",
                  "error",
                )
              }
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              variant="standard"
              fullWidth
              required
              label="Verification Code"
              inputProps={{ maxLength: 6 }}
              InputLabelProps={{ shrink: true }}
              value={otp.code}
              onChange={(e) =>
                setOtp({ ...otp, code: e.target.value.replace(/\D/g, "") })
              }
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="New Password"
              type="password"
              helperText="Minimum 8 characters"
              InputLabelProps={{ shrink: true }}
              value={otp.next}
              onChange={(e) => setOtp({ ...otp, next: e.target.value })}
            />
            <TextField
              variant="standard"
              fullWidth
              required
              label="Confirm New Password"
              type="password"
              error={otp.confirm.length > 0 && !otpMatch}
              helperText={
                otp.confirm.length > 0 && !otpMatch
                  ? "Passwords don't match"
                  : " "
              }
              InputLabelProps={{ shrink: true }}
              value={otp.confirm}
              onChange={(e) => setOtp({ ...otp, confirm: e.target.value })}
            />
          </Box>
          <Button
            variant="contained"
            sx={primaryBtn}
            disabled={!otpReady || otpReset.isPending}
            onClick={() => otpReset.mutate()}
          >
            Submit
          </Button>
        </Paper>
      </Stack>
      {ToastEl}
    </Box>
  );
}
